/**
 * Logger utility to centralize and control logging throughout the application.
 * In production, logs can be disabled or redirected to a monitoring service.
 */
import * as Sentry from '@sentry/browser';

// Initialize Sentry for error monitoring in production
if (import.meta.env.PROD || process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    // Enable automatic session tracking
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session replay configuration
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: 0.1,
    // Session replay for errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Check if we're in production
const isProduction = import.meta.env.PROD || process.env.NODE_ENV === 'production';

// Enable/disable different log levels based on environment
const config = {
  // Set to false to disable all logs in production
  enableLogsInProduction: false,

  // Enable specific log levels in production (only relevant if enableLogsInProduction is true)
  levels: {
    debug: !isProduction,
    info: !isProduction,
    log: !isProduction,
    warn: !isProduction,
    error: isProduction, // Keep errors enabled in production by default
  },

  // Add prefix to all logs for better identification
  prefix: isProduction ? '[HansCard]' : '[HansCard-Dev]',
};

/**
 * Main logger object that wraps console methods
 */
const logger = {
  /**
   * Log debug information (disabled in production)
   */
  debug: (...args: any[]): void => {
    if (!isProduction || (config.enableLogsInProduction && config.levels.debug)) {
      console.debug(`${config.prefix} DEBUG:`, ...args);
    }
  },

  /**
   * Log general information (disabled in production)
   */
  info: (...args: any[]): void => {
    if (!isProduction || (config.enableLogsInProduction && config.levels.info)) {
      console.info(`${config.prefix} INFO:`, ...args);
    }
  },

  /**
   * Standard log (disabled in production)
   */
  log: (...args: any[]): void => {
    if (!isProduction || (config.enableLogsInProduction && config.levels.log)) {
      console.log(`${config.prefix} LOG:`, ...args);
    }
  },

  /**
   * Log warnings (disabled in production unless configured)
   */
  warn: (...args: any[]): void => {
    if (!isProduction || (config.enableLogsInProduction && config.levels.warn)) {
      console.warn(`${config.prefix} WARN:`, ...args);
    }
  },

  /**
   * Log errors (enabled in production by default)
   */
  error: (...args: any[]): void => {
    if (!isProduction || (config.enableLogsInProduction && config.levels.error)) {
      console.error(`${config.prefix} ERROR:`, ...args);

      // In production, send errors to Sentry
      if (isProduction) {
        const [firstArg, ...restArgs] = args;
        if (firstArg instanceof Error) {
          Sentry.captureException(firstArg, {
            extra: { additionalInfo: restArgs }
          });
        } else {
          Sentry.captureMessage(String(firstArg), {
            level: 'error',
            extra: { additionalInfo: restArgs }
          });
        }
      }
    }
  },

  /**
   * Create a logger with a specific context
   */
  getContextLogger: (context: string) => {
    return {
      debug: (...args: any[]): void => logger.debug(`[${context}]`, ...args),
      info: (...args: any[]): void => logger.info(`[${context}]`, ...args),
      log: (...args: any[]): void => logger.log(`[${context}]`, ...args),
      warn: (...args: any[]): void => logger.warn(`[${context}]`, ...args),
      error: (...args: any[]): void => logger.error(`[${context}]`, ...args),
    };
  },
};

// Export both default and named
export { logger };
export default logger;